import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Page,
  Card,
  Banner,
  Button as PolarisButton,
  TextField,
  Text,
  Badge as PolarisBadge,
  Checkbox,
  Spinner,
} from "@shopify/polaris";
import UserSelect from "features/user-select/user-select";
import useIsPhone from "features/files/use-is-phone";
import DataTable from "components/datatable/Datatable";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchTrainingTest,
  updateTrainingTest,
  saveTrainingQuestions,
  setTrainingTestRoles,
  removeTrainingMaterial,
  generateTrainingQuestions,
  assignTrainingUsers,
  removeTrainingAssignment,
  fetchTrainingAssignments,
  newBlankQuestion,
  normalizeQuestionCorrectIds,
  TrainingTest,
  TrainingQuestion,
  TrainingAssignment,
} from "services/work-security-training.service";
import { fetchWorkSecurityRoles, resolveWorkSecurityUploadCategoryIds, WorkSecurityRole } from "services/work-security.service";
import { fetchById as fetchCompanyById } from "services/company.service";
import {
  ExamHeader,
  ExamLegend,
  ExamQuestionGrid,
  ExamQuestionStatus,
  optionLetter,
} from "containers/my-training-container/training-exam-ui";
import TrainingTestStepProgress from "./TrainingTestStepProgress";
import {
  TRAINING_TEST_STEPS,
  TrainingTestStep,
  canActivateTrainingTest,
  getTrainingStepCompletion,
  parseTrainingTestStep,
  stepIndex,
} from "./training-test-steps";

function statusTone(status: string): any {
  if (status === "active") return "success";
  if (status === "passed") return "success";
  if (status === "failed") return "critical";
  if (status === "in_progress") return "attention";
  return undefined;
}

export default function WorkSecurityTrainingViewContainer() {
  const { companyId, testId } = useParams<{ companyId: string; testId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  const currentStep = parseTrainingTestStep(searchParams.get("step"));

  const [test, setTest] = useState<TrainingTest | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [passPercent, setPassPercent] = useState("80");
  const [durationMinutes, setDurationMinutes] = useState("30");
  const [maxAttempts, setMaxAttempts] = useState("");
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [editorIndex, setEditorIndex] = useState(0);
  const [roles, setRoles] = useState<WorkSecurityRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [aiSourceText, setAiSourceText] = useState("");
  const [aiCount, setAiCount] = useState("5");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<string[]>([]);
  const materialSelectionTouchedRef = useRef(false);
  const [aiSkipNotice, setAiSkipNotice] = useState("");
  const [assignUserId, setAssignUserId] = useState<string | null>(null);
  const [aiQuestionsEnabled, setAiQuestionsEnabled] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [uploadCategoryIds, setUploadCategoryIds] = useState<string[]>([]);

  const load = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!companyId || !testId) return;
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
        materialSelectionTouchedRef.current = false;
      }
      setError("");
      try {
        const [
          { test: detail, aiQuestionsEnabled: aiEnabled, uploadCategoryIds: testUploadCategoryIds },
          rolesResult,
          assignData,
        ] = await Promise.all([
          fetchTrainingTest(companyId, testId),
          fetchWorkSecurityRoles(companyId, { page: 1, pageSize: 200 }),
          fetchTrainingAssignments(companyId, testId, { page: 1, pageSize: 50 }),
        ]);
        setTest(detail);
        setUploadCategoryIds((prev) => {
          const fromTest = resolveWorkSecurityUploadCategoryIds({
            uploadCategoryIds: testUploadCategoryIds,
          });
          return fromTest.length ? fromTest : prev;
        });
        setAiQuestionsEnabled(!!aiEnabled);
        if (!aiEnabled) setShowAiPanel(false);
        setName(detail.name || "");
        setDescription(detail.description || "");
        setPassPercent(String(detail.passPercent ?? 80));
        setDurationMinutes(String(detail.durationMinutes ?? 30));
        setMaxAttempts(
          detail.maxAttempts == null ? "" : String(detail.maxAttempts)
        );
        setQuestions(
          (detail.questions || []).length
            ? detail.questions!.map((q) => ({
                ...q,
                correctOptionIds: normalizeQuestionCorrectIds(q as any),
              }))
            : [newBlankQuestion()]
        );
        setEditorIndex((prev) => {
          const count = (detail.questions || []).length || 1;
          if (silent) {
            return Math.min(prev, Math.max(0, count - 1));
          }
          return 0;
        });
        setSelectedRoleIds((detail.roles || []).map((r) => r.id));
        const roleList = Array.isArray(rolesResult)
          ? rolesResult
          : rolesResult.entries || [];
        setRoles(roleList);
        setAssignments(assignData.assignments || []);
        const materialIds = (detail.materials || []).map((m) => m.id);
        setSelectedMaterialIds((prev) => {
          if (!materialSelectionTouchedRef.current) return materialIds;
          return prev.filter((id) => materialIds.includes(id));
        });
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            t("load_failed", { defaultValue: "Failed to load test" })
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyId, testId, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    fetchCompanyById(companyId)
      .then((data: any) => {
        if (cancelled) return;
        const company = data?.companyEntity || data?.company || data;
        setUploadCategoryIds((prev) => {
          const fromCompany = resolveWorkSecurityUploadCategoryIds(company);
          return fromCompany.length ? fromCompany : prev;
        });
      })
      .catch(() => {
        if (!cancelled) return;
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleOpenSelectDocument = async () => {
    if (!testId || !companyId) return;

    let categoryIds = uploadCategoryIds;
    try {
      const [{ uploadCategoryIds: freshIds }, companyData] = await Promise.all([
        fetchTrainingTest(companyId, testId),
        fetchCompanyById(companyId),
      ]);
      const company = companyData?.companyEntity || companyData?.company || companyData;
      categoryIds = resolveWorkSecurityUploadCategoryIds({
        uploadCategoryIds: freshIds,
        ...company,
      });
      if (categoryIds.length) {
        setUploadCategoryIds(categoryIds);
      }
    } catch {
      // Fall back to state already loaded on the page.
    }

    if (!categoryIds.length) {
      setError(
        t("configure_upload_category_to_select", {
          defaultValue:
            "Configure the upload category in Settings before selecting an existing document.",
        })
      );
      return;
    }
    const attachedMaterialFileIds = (test?.materials || [])
      .map((m) => {
        const file = m.file;
        if (!file) return null;
        return typeof file === "object" ? file.id : String(file);
      })
      .filter(Boolean) as string[];

    dispatch(
      setModalxOptions({
        title: t("select_existing_document", { defaultValue: "Select Document" }),
      })
    );
    dispatch(setModalxComponent("WorkSecuritySelectDocumentModal"));
    dispatch(
      setModalxComponentProps({
        mode: "training",
        testId,
        uploadCategoryIds: categoryIds,
        attachedMaterialFileIds,
        onSuccess: () => {
          void load({ silent: true });
        },
      })
    );
  };

  const handleSaveMeta = async () => {
    if (!companyId || !testId) return;
    setBusy(true);
    setError("");
    try {
      await updateTrainingTest(companyId, testId, {
        name: name.trim(),
        description: description.trim() || null,
        passPercent: Number(passPercent) || 80,
        durationMinutes: Number(durationMinutes) || 30,
        maxAttempts: maxAttempts.trim() ? Number(maxAttempts) : null,
      });
      await load({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveQuestions = async () => {
    if (!companyId || !testId) return;
    setBusy(true);
    setError("");
    try {
      await saveTrainingQuestions(companyId, testId, questions);
      await load({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save questions");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!companyId || !testId) return;
    setBusy(true);
    setError("");
    try {
      await setTrainingTestRoles(companyId, testId, selectedRoleIds);
      await load({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save roles");
    } finally {
      setBusy(false);
    }
  };

  const handleActivate = async () => {
    if (!companyId || !testId || !name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await saveTrainingQuestions(companyId, testId, questions);
      await setTrainingTestRoles(companyId, testId, selectedRoleIds);
      await updateTrainingTest(companyId, testId, {
        name: name.trim(),
        description: description.trim() || null,
        passPercent: Number(passPercent) || 80,
        durationMinutes: Number(durationMinutes) || 30,
        maxAttempts: maxAttempts.trim() ? Number(maxAttempts) : null,
        status: "active",
      });
      await load({ silent: true });
      goToStep("review");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to activate");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!companyId || !testId) return;
    setBusy(true);
    try {
      await updateTrainingTest(companyId, testId, { status: "archived" });
      await load({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to archive");
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateAi = async () => {
    if (!companyId || !testId) return;
    setBusy(true);
    setError("");
    setAiSkipNotice("");
    try {
      const {
        questions: drafts,
        usedMaterials,
        skippedMaterials,
      } = await generateTrainingQuestions(companyId, testId, {
        count: Number(aiCount) || 5,
        sourceText: aiSourceText.trim() || undefined,
        materialIds: selectedMaterialIds,
      });
      setQuestions((prev) => {
        const keep = prev.filter((q) => q.questionText.trim());
        const normalized = drafts.map((d: any) => ({
          ...d,
          correctOptionIds: Array.isArray(d.correctOptionIds)
            ? d.correctOptionIds
            : d.correctOptionId
              ? [d.correctOptionId]
              : [],
        }));
        return [...keep, ...normalized];
      });
      if (skippedMaterials && skippedMaterials.length) {
        const names = skippedMaterials
          .map((s) => s.name)
          .filter(Boolean)
          .join(", ");
        setAiSkipNotice(
          t("ai_materials_skipped", {
            defaultValue:
              "{{used}} document(s) used; {{skipped}} skipped (no readable text): {{names}}",
            used: usedMaterials?.length || 0,
            skipped: skippedMaterials.length,
            names: names || "—",
          })
        );
      } else if (usedMaterials && usedMaterials.length) {
        const viaPdf = usedMaterials.some((m: any) => m.mode === "pdf_document");
        setAiSkipNotice(
          viaPdf
            ? t("ai_materials_used_pdf_scan", {
                defaultValue:
                  "Generated from {{count}} document(s), including scanned PDF(s) read by AI.",
                count: usedMaterials.length,
              })
            : t("ai_materials_used", {
                defaultValue: "Generated from {{count}} document(s).",
                count: usedMaterials.length,
              })
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("ai_generate_failed", {
            defaultValue: "Could not generate questions",
          })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAssignUser = async () => {
    if (!companyId || !testId || !assignUserId) return;
    setBusy(true);
    try {
      await assignTrainingUsers(companyId, testId, [assignUserId]);
      setAssignUserId(null);
      await load({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!companyId) return;
    setBusy(true);
    setError("");
    try {
      await removeTrainingAssignment(companyId, assignmentId);
      await load({ silent: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("unassign_failed", { defaultValue: "Failed to remove user" })
      );
    } finally {
      setBusy(false);
    }
  };

  const goToStep = (step: TrainingTestStep) => {
    setSearchParams({ step }, { replace: true });
  };

  const goToAdjacentStep = (delta: number) => {
    const idx = stepIndex(currentStep);
    const next = TRAINING_TEST_STEPS[idx + delta];
    if (next) goToStep(next);
  };

  const stepCompletion = useMemo(
    () =>
      getTrainingStepCompletion({
        test: test ? { ...test, name } : test,
        questions,
        selectedRoleIds,
        assignmentsCount: assignments.length,
      }),
    [test, name, questions, selectedRoleIds, assignments.length]
  );

  const canActivate = canActivateTrainingTest(stepCompletion);

  const renderStepNav = (options?: { nextLabel?: string }) => {
    const idx = stepIndex(currentStep);
    const hasPrev = idx > 0;
    const hasNext = idx < TRAINING_TEST_STEPS.length - 1;
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-200 pt-3 dark:border-zinc-700">
        <PolarisButton disabled={!hasPrev} onClick={() => goToAdjacentStep(-1)}>
          {t("previous", { defaultValue: "Previous" })}
        </PolarisButton>
        {hasNext ? (
          <PolarisButton variant="primary" onClick={() => goToAdjacentStep(1)}>
            {options?.nextLabel || t("next", { defaultValue: "Next" })}
          </PolarisButton>
        ) : (
          <span />
        )}
      </div>
    );
  };

  const assignmentColumns = useMemo(
    () => [
      {
        key: "userName",
        name: t("user", { defaultValue: "User" }),
      },
      {
        key: "status",
        name: t("status", { defaultValue: "Status" }),
        type: "custom" as const,
        render: (item: TrainingAssignment) => (
          <PolarisBadge tone={statusTone(item.status)}>
            {item.status}
          </PolarisBadge>
        ),
      },
      {
        key: "source",
        name: t("source", { defaultValue: "Source" }),
      },
      {
        key: "attemptsCount",
        name: t("attempts", { defaultValue: "Attempts" }),
      },
      {
        key: "lastScore",
        name: t("last_score", { defaultValue: "Last score" }),
      },
      {
        key: "lastSubmittedAt",
        name: t("submitted_at", { defaultValue: "Submitted" }),
        type: "datetime" as const,
      },
      {
        key: "actions",
        name: "",
        type: "custom" as const,
        render: (item: TrainingAssignment & { canRemove?: boolean }) =>
          item.canRemove ? (
            <PolarisButton
              tone="critical"
              variant="plain"
              loading={busy}
              onClick={() => handleRemoveAssignment(item.id)}
            >
              {t("remove", { defaultValue: "Remove" })}
            </PolarisButton>
          ) : item.attemptsCount && item.attemptsCount > 0 ? (
            <Text as="span" variant="bodySm" tone="subdued">
              {t("cannot_unassign_has_attempts", {
                defaultValue: "Already attempted",
              })}
            </Text>
          ) : null,
      },
    ],
    [t, busy]
  );

  const updateQuestion = (index: number, patch: Partial<TrainingQuestion>) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, ...patch } : q))
    );
  };

  const updateOption = (
    qIndex: number,
    optIndex: number,
    text: string
  ) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const options = q.options.map((o, oi) =>
          oi === optIndex ? { ...o, text } : o
        );
        return { ...q, options };
      })
    );
  };

  const primaryActions = useMemo(() => {
    if (!test) return [];
    if (test.status === "active") {
      return [
        {
          content: t("archive", { defaultValue: "Archive" }),
          onAction: handleArchive,
          loading: busy,
        },
      ];
    }
    return [];
  }, [test, busy, t]);

  if (loading && !test) {
    return (
      <Page fullWidth title={t("training_test", { defaultValue: "Training test" })}>
        <div className="flex justify-center py-20">
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!test) {
    return (
      <Page
        fullWidth
        title={t("training_test", { defaultValue: "Training test" })}
        backAction={{
          onAction: () => navigate(`/app/${companyId}/work-security/training`),
        }}
      >
        <Banner tone="critical">{error || "Not found"}</Banner>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title={test.name}
      backAction={{
        onAction: () => navigate(`/app/${companyId}/work-security/training`),
      }}
      titleMetadata={
        <div className="flex items-center gap-2">
          {refreshing ? (
            <Spinner
              size="small"
              accessibilityLabel={t("refreshing", {
                defaultValue: "Refreshing",
              })}
            />
          ) : null}
          <PolarisBadge tone={statusTone(test.status)}>{test.status}</PolarisBadge>
        </div>
      }
      primaryAction={primaryActions[0]}
    >
      <div className="space-y-4 pb-10">
        {error && (
          <Banner tone="critical" onDismiss={() => setError("")}>
            {error}
          </Banner>
        )}

        <TrainingTestStepProgress
          currentStep={currentStep}
          completion={stepCompletion}
          onStepSelect={goToStep}
        />

        {currentStep === "basics" && (
        <Card>
          <div className="p-4 space-y-3">
            <Text as="h2" variant="headingMd">
              {t("training_step_basics", { defaultValue: "Basics" })}
            </Text>
            <TextField
              label={t("name", { defaultValue: "Name" })}
              value={name}
              onChange={setName}
              autoComplete="off"
            />
            <TextField
              label={t("description", { defaultValue: "Description" })}
              value={description}
              onChange={setDescription}
              multiline={3}
              autoComplete="off"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextField
                label={t("pass_percent", { defaultValue: "Pass %" })}
                type="number"
                value={passPercent}
                onChange={setPassPercent}
                autoComplete="off"
              />
              <TextField
                label={t("duration_minutes", { defaultValue: "Duration (min)" })}
                type="number"
                value={durationMinutes}
                onChange={setDurationMinutes}
                autoComplete="off"
              />
              <TextField
                label={t("max_attempts", {
                  defaultValue: "Max attempts (empty = unlimited)",
                })}
                type="number"
                value={maxAttempts}
                onChange={setMaxAttempts}
                autoComplete="off"
              />
            </div>
            <div className="flex justify-end">
              <PolarisButton onClick={handleSaveMeta} loading={busy}>
                {t("save_settings", { defaultValue: "Save settings" })}
              </PolarisButton>
            </div>
            {renderStepNav()}
          </div>
        </Card>
        )}

        {currentStep === "materials" && (
        <Card>
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <Text as="h2" variant="headingMd">
                  {t("training_materials", { defaultValue: "Training materials" })}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("training_materials_optional_hint", {
                    defaultValue:
                      "Optional — attach documents workers can review before the test.",
                  })}
                </Text>
              </div>
              <PolarisButton onClick={handleOpenSelectDocument} disabled={busy}>
                {t("select_existing_document", {
                  defaultValue: "Select Document",
                })}
              </PolarisButton>
            </div>

            {!uploadCategoryIds.length && (
              <Banner tone="warning">
                {t("configure_upload_category_to_select", {
                  defaultValue:
                    "Configure the upload category in Settings before selecting an existing document.",
                })}
              </Banner>
            )}

            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-700">
              {(test.materials || []).length === 0 ? (
                <div className="text-center py-8 px-4 text-sm text-zinc-500">
                  {t("no_materials_yet", {
                    defaultValue: "No materials attached yet.",
                  })}
                </div>
              ) : (
                <DataTable
                  collection={(test.materials || []).map((m) => ({
                    ...m,
                    fileName:
                      m.file?.title || m.file?.name || m.file?.id || m.id,
                    createdAtIso: m.createdAt
                      ? new Date(m.createdAt).toISOString()
                      : "",
                  }))}
                  columns={[
                    {
                      key: "fileName",
                      name: t("document", { defaultValue: "Document" }),
                    },
                    {
                      key: "createdAtIso",
                      name: t("created_at", { defaultValue: "Created At" }),
                      type: "datetime",
                    },
                    {
                      key: "actions",
                      name: "",
                      type: "custom",
                      render: (item: any) => (
                        <PolarisButton
                          tone="critical"
                          variant="plain"
                          onClick={async () => {
                            if (!companyId || !testId) return;
                            setBusy(true);
                            try {
                              await removeTrainingMaterial(
                                companyId,
                                testId,
                                item.id
                              );
                              await load({ silent: true });
                            } catch (err: any) {
                              setError(
                                err?.response?.data?.message ||
                                  t("remove_failed", {
                                    defaultValue: "Failed to remove",
                                  })
                              );
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          {t("remove", { defaultValue: "Remove" })}
                        </PolarisButton>
                      ),
                    },
                  ]}
                />
              )}
            </div>
            {renderStepNav()}
          </div>
        </Card>
        )}

        {currentStep === "questions" && (
        <Card>
          <div className="p-3 sm:p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Text as="h2" variant="headingMd">
                {t("questions", { defaultValue: "Questions" })}
              </Text>
              <div className="flex flex-wrap gap-2">
                <PolarisButton
                  onClick={() => {
                    setQuestions((prev) => [...prev, newBlankQuestion()]);
                    setEditorIndex(questions.length);
                  }}
                >
                  {t("add_question", { defaultValue: "Add question" })}
                </PolarisButton>
                <PolarisButton
                  variant="primary"
                  onClick={handleSaveQuestions}
                  loading={busy}
                >
                  {t("save_questions", { defaultValue: "Save questions" })}
                </PolarisButton>
              </div>
            </div>

            {aiQuestionsEnabled ? (
              <div className="rounded-xl border border-dashed border-zinc-300 p-3 space-y-2 dark:border-zinc-600">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("ai_generate_optional_hint", {
                      defaultValue:
                        "Optionally draft questions with AI from your training materials.",
                    })}
                  </Text>
                  <PolarisButton onClick={() => setShowAiPanel((v) => !v)}>
                    {showAiPanel
                      ? t("hide_ai_generate", {
                          defaultValue: "Hide AI generate",
                        })
                      : t("show_ai_generate", {
                          defaultValue: "Generate with AI",
                        })}
                  </PolarisButton>
                </div>
                {showAiPanel ? (
                  <>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t("ai_generate_hint", {
                        defaultValue:
                          "Select attached materials (PDF/TXT with text). Questions are generated in Lithuanian. Review before saving.",
                      })}
                    </Text>

                    {(test.materials || []).length === 0 ? (
                      <Banner tone="warning">
                        {t("ai_attach_materials_first", {
                          defaultValue:
                            "Attach training materials in the Materials step so AI can read document content.",
                        })}
                      </Banner>
                    ) : (
                      <div className="space-y-2 rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            {t("ai_select_materials", {
                              defaultValue: "Documents to use",
                            })}
                          </Text>
                          <div className="flex gap-2">
                            <PolarisButton
                              size="slim"
                              onClick={() => {
                                materialSelectionTouchedRef.current = true;
                                setSelectedMaterialIds(
                                  (test.materials || []).map((m) => m.id)
                                );
                              }}
                            >
                              {t("select_all", { defaultValue: "Select all" })}
                            </PolarisButton>
                            <PolarisButton
                              size="slim"
                              onClick={() => {
                                materialSelectionTouchedRef.current = true;
                                setSelectedMaterialIds([]);
                              }}
                            >
                              {t("clear", { defaultValue: "Clear" })}
                            </PolarisButton>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-auto">
                          {(test.materials || []).map((m) => {
                            const label =
                              m.file?.title ||
                              m.file?.name ||
                              m.file?.id ||
                              m.id;
                            return (
                              <Checkbox
                                key={m.id}
                                label={label}
                                checked={selectedMaterialIds.includes(m.id)}
                                onChange={(checked) => {
                                  materialSelectionTouchedRef.current = true;
                                  setSelectedMaterialIds((prev) =>
                                    checked
                                      ? [...prev, m.id]
                                      : prev.filter((id) => id !== m.id)
                                  );
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <TextField
                      label={t("source_text", {
                        defaultValue: "Extra source text (optional)",
                      })}
                      value={aiSourceText}
                      onChange={setAiSourceText}
                      multiline={3}
                      autoComplete="off"
                      helpText={t("source_text_help", {
                        defaultValue:
                          "Optional paste if a document has no text layer.",
                      })}
                    />
                    <div className="flex gap-2 items-end">
                      <TextField
                        label={t("count", { defaultValue: "Count" })}
                        type="number"
                        value={aiCount}
                        onChange={setAiCount}
                        autoComplete="off"
                      />
                      <PolarisButton
                        onClick={handleGenerateAi}
                        loading={busy}
                        disabled={
                          selectedMaterialIds.length === 0 &&
                          !aiSourceText.trim()
                        }
                      >
                        {t("generate_with_ai", {
                          defaultValue: "Generate with AI",
                        })}
                      </PolarisButton>
                    </div>
                    {aiSkipNotice ? (
                      <Banner
                        tone="info"
                        onDismiss={() => setAiSkipNotice("")}
                      >
                        {aiSkipNotice}
                      </Banner>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {(() => {
              const safeIndex = Math.min(
                editorIndex,
                Math.max(0, questions.length - 1)
              );
              const q = questions[safeIndex];
              if (!q) return null;

              const getEditorStatus = (i: number): ExamQuestionStatus => {
                const item = questions[i];
                if (!item) return "not_attempted";
                if (i === safeIndex) return "current";
                if (
                  item.questionText.trim() &&
                  (item.correctOptionIds || []).length > 0 &&
                  item.options.every((o) => o.text.trim())
                ) {
                  return "answered";
                }
                if (item.questionText.trim() || item.options.some((o) => o.text.trim())) {
                  return "not_answered";
                }
                return "not_attempted";
              };

              return (
                <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-950">
                  <ExamHeader
                    title={
                      name.trim() ||
                      t("question_editor", {
                        defaultValue: "Question editor",
                      })
                    }
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px]">
                    <div className="flex min-h-[480px] flex-col border-zinc-200 dark:border-zinc-700 lg:border-r">
                      <div className="flex-1 space-y-4 p-3 sm:p-5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold">
                            {t("question_n", {
                              defaultValue: "Question {{n}}",
                              n: safeIndex + 1,
                            })}
                          </p>
                          <PolarisButton
                            tone="critical"
                            variant="plain"
                            disabled={questions.length <= 1}
                            onClick={() => {
                              setQuestions((prev) =>
                                prev.filter((_, i) => i !== safeIndex)
                              );
                              setEditorIndex((i) => Math.max(0, i - 1));
                            }}
                          >
                            {t("remove", { defaultValue: "Remove" })}
                          </PolarisButton>
                        </div>

                        <TextField
                          label={t("question", { defaultValue: "Question" })}
                          value={q.questionText}
                          onChange={(v) =>
                            updateQuestion(safeIndex, { questionText: v })
                          }
                          multiline={4}
                          autoComplete="off"
                        />

                        <Text as="p" variant="bodySm" tone="subdued">
                          {t("mark_correct_hint", {
                            defaultValue:
                              "Tick one or more options as correct. Workers can select multiple when more than one is correct.",
                          })}
                        </Text>

                        <div className="space-y-2">
                          {q.options.map((opt, oi) => {
                            const correctIds = q.correctOptionIds || [];
                            const isCorrectMarked = correctIds.includes(opt.id);
                            const removeOption = () => {
                              setQuestions((prev) =>
                                prev.map((qq, i) => {
                                  if (i !== safeIndex) return qq;
                                  const options = qq.options.filter(
                                    (_, j) => j !== oi
                                  );
                                  const nextCorrect = (
                                    qq.correctOptionIds || []
                                  ).filter((id) => id !== opt.id);
                                  return {
                                    ...qq,
                                    options,
                                    correctOptionIds:
                                      nextCorrect.length > 0
                                        ? nextCorrect
                                        : options[0]
                                          ? [options[0].id]
                                          : [],
                                  };
                                })
                              );
                            };
                            return (
                              <div
                                key={opt.id}
                                className={`flex items-start gap-2 rounded-md border px-2 py-2 sm:px-3 ${
                                  isCorrectMarked
                                    ? "border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950/30"
                                    : "border-zinc-200 dark:border-zinc-700"
                                }`}
                              >
                                {/* The letter ticks the box too: on a touch screen the box alone is easy to miss. */}
                                <label className="flex min-h-10 shrink-0 cursor-pointer items-center gap-2 lg:min-h-0 lg:items-start lg:gap-0">
                                  <input
                                    type="checkbox"
                                    className="h-6 w-6 lg:mt-2 lg:h-auto lg:w-auto"
                                    checked={isCorrectMarked}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      updateQuestion(safeIndex, {
                                        correctOptionIds: checked
                                          ? [...correctIds, opt.id]
                                          : correctIds.filter(
                                              (id) => id !== opt.id
                                            ),
                                      });
                                    }}
                                    aria-label={t("mark_correct", {
                                      defaultValue: "Mark as correct",
                                    })}
                                  />
                                  <span className="w-5 text-sm font-semibold text-zinc-500 lg:mt-2 lg:ml-2">
                                    {optionLetter(oi)}.
                                  </span>
                                </label>
                                <div className="min-w-0 flex-1">
                                  <TextField
                                    label={`${t("option", {
                                      defaultValue: "Option",
                                    })} ${oi + 1}`}
                                    labelHidden
                                    value={opt.text}
                                    onChange={(v) =>
                                      updateOption(safeIndex, oi, v)
                                    }
                                    autoComplete="off"
                                    placeholder={t("option_placeholder", {
                                      defaultValue: "Answer option…",
                                    })}
                                  />
                                </div>
                                {q.options.length > 2 &&
                                  (isPhone ? (
                                    <button
                                      type="button"
                                      aria-label={t("remove", { defaultValue: "Remove" })}
                                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-xl leading-none text-red-700 hover:bg-red-50"
                                      onClick={removeOption}
                                    >
                                      ×
                                    </button>
                                  ) : (
                                    <PolarisButton
                                      variant="plain"
                                      tone="critical"
                                      onClick={removeOption}
                                    >
                                      ×
                                    </PolarisButton>
                                  ))}
                              </div>
                            );
                          })}
                        </div>

                        <PolarisButton
                          size="slim"
                          onClick={() => {
                            const id = `opt-${Date.now()}-${safeIndex}`;
                            updateQuestion(safeIndex, {
                              options: [...q.options, { id, text: "" }],
                            });
                          }}
                        >
                          {t("add_option", { defaultValue: "Add option" })}
                        </PolarisButton>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                        <button
                          type="button"
                          onClick={() => {
                            setQuestions((prev) => [
                              ...prev,
                              newBlankQuestion(),
                            ]);
                            setEditorIndex(questions.length);
                          }}
                          className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 max-md:min-h-11"
                        >
                          {t("add_question", { defaultValue: "Add question" })}
                        </button>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={safeIndex === 0}
                            onClick={() =>
                              setEditorIndex(Math.max(0, safeIndex - 1))
                            }
                            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-sky-700 max-md:min-h-11"
                          >
                            {t("previous", { defaultValue: "Previous" })}
                          </button>
                          <button
                            type="button"
                            disabled={safeIndex >= questions.length - 1}
                            onClick={() =>
                              setEditorIndex(
                                Math.min(questions.length - 1, safeIndex + 1)
                              )
                            }
                            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-sky-700 max-md:min-h-11"
                          >
                            {t("next", { defaultValue: "Next" })}
                          </button>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={handleSaveQuestions}
                          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 max-md:min-h-11"
                        >
                          {t("save_questions", {
                            defaultValue: "Save questions",
                          })}
                        </button>
                      </div>
                    </div>

                    <aside className="order-first space-y-3 bg-zinc-50 p-3 dark:bg-zinc-900/50 lg:order-none">
                      <div className="rounded-lg border border-zinc-200 bg-white px-3 py-3 text-center text-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <p className="text-xs uppercase text-zinc-500">
                          {t("duration_minutes", {
                            defaultValue: "Duration (min)",
                          })}
                        </p>
                        <p className="mt-1 text-2xl font-bold">
                          {durationMinutes || "—"}
                        </p>
                      </div>
                      <ExamQuestionGrid
                        count={questions.length}
                        currentIndex={safeIndex}
                        getStatus={getEditorStatus}
                        onSelect={setEditorIndex}
                      />
                    </aside>
                  </div>
                  <div className="border-t border-zinc-200 p-3 dark:border-zinc-700">
                    <ExamLegend />
                  </div>
                </div>
              );
            })()}
            {renderStepNav()}
          </div>
        </Card>
        )}

        {currentStep === "audience" && (
        <>
        <Card>
          <div className="p-4 space-y-3">
            <Text as="h2" variant="headingMd">
              {t("linked_roles", { defaultValue: "Linked roles" })}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t("linked_roles_hint", {
                defaultValue:
                  "Users assigned to these roles are auto-enrolled when the test is active.",
              })}
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-auto">
              {roles.map((role) => (
                <Checkbox
                  key={role.id}
                  label={role.name}
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={(checked) => {
                    setSelectedRoleIds((prev) =>
                      checked
                        ? [...prev, role.id]
                        : prev.filter((id) => id !== role.id)
                    );
                  }}
                />
              ))}
            </div>
            <div className="flex justify-end">
              <PolarisButton onClick={handleSaveRoles} loading={busy}>
                {t("save_roles", { defaultValue: "Save role links" })}
              </PolarisButton>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4 space-y-3">
            <Text as="h2" variant="headingMd">
              {t("manual_assign", { defaultValue: "Assign extra users" })}
            </Text>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="min-w-[240px] flex-1">
                <UserSelect
                  value={assignUserId || ""}
                  onChange={(id: string) => setAssignUserId(id || null)}
                  onlyActivated
                  showUserEmail
                  label={t("select_user", { defaultValue: "Select user" })}
                />
              </div>
              <PolarisButton
                onClick={handleAssignUser}
                disabled={
                  !assignUserId ||
                  assignments.some((a) => {
                    const uid =
                      typeof a.user === "object" ? a.user?.id : a.user;
                    return uid && String(uid) === String(assignUserId);
                  })
                }
                loading={busy}
              >
                {t("assign", { defaultValue: "Assign" })}
              </PolarisButton>
            </div>
            {assignUserId &&
            assignments.some((a) => {
              const uid = typeof a.user === "object" ? a.user?.id : a.user;
              return uid && String(uid) === String(assignUserId);
            }) ? (
              <Banner tone="info">
                {t("user_already_assigned_to_test", {
                  defaultValue: "This user is already assigned to this test.",
                })}
              </Banner>
            ) : (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("training_audience_hint", {
                  defaultValue:
                    "Link at least one role or assign users manually before activating.",
                })}
              </Text>
            )}

            <div className="pt-2">
              <Text as="h3" variant="headingSm">
                {t("assigned_users", {
                  defaultValue: "Assigned users",
                })}{" "}
                <span className="text-zinc-400 font-normal">
                  ({assignments.length})
                </span>
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t("training_unassign_hint", {
                  defaultValue:
                    "You can remove users who have not submitted any attempts yet.",
                })}
              </Text>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
              {assignments.length === 0 ? (
                <div className="text-center py-10 px-4 text-sm text-zinc-500">
                  {t("no_assignments_yet", {
                    defaultValue: "No assignments yet.",
                  })}
                </div>
              ) : (
                <DataTable
                  collection={assignments.map((a) => ({
                    ...a,
                    userName: a.user?.name || a.user?.email || "—",
                    lastScore:
                      a.lastAttempt?.scorePercent != null
                        ? `${a.lastAttempt.scorePercent}%`
                        : "—",
                    lastSubmittedAt: a.lastAttempt?.submittedAt
                      ? new Date(a.lastAttempt.submittedAt).toISOString()
                      : "",
                  }))}
                  columns={assignmentColumns}
                />
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">{renderStepNav({ nextLabel: t("review", { defaultValue: "Review" }) })}</div>
        </Card>
        </>
        )}

        {currentStep === "review" && (
        <>
        <Card>
          <div className="p-4 space-y-4">
            <Text as="h2" variant="headingMd">
              {t("training_step_review", { defaultValue: "Review" })}
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("name", { defaultValue: "Name" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">{name.trim() || "—"}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("pass_percent", { defaultValue: "Pass %" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">{passPercent}%</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("duration_minutes", { defaultValue: "Duration (min)" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">{durationMinutes}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("questions", { defaultValue: "Questions" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">
                  {test.questionsCount ?? test.questions?.length ?? 0}
                </p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("linked_roles", { defaultValue: "Linked roles" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">{selectedRoleIds.length}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                <p className="font-medium">{t("assignments", { defaultValue: "Assignments" })}</p>
                <p className="text-zinc-600 dark:text-zinc-300">{assignments.length}</p>
              </div>
            </div>

            {test.status === "draft" && !canActivate ? (
              <Banner tone="warning">
                {t("training_activate_requirements", {
                  defaultValue:
                    "Complete basics, save at least one valid question, and link roles or assign users before activating.",
                })}
              </Banner>
            ) : null}

            {test.status === "draft" ? (
              <div className="flex justify-end">
                <PolarisButton
                  variant="primary"
                  onClick={handleActivate}
                  loading={busy}
                  disabled={!canActivate}
                >
                  {t("activate_test", { defaultValue: "Activate test" })}
                </PolarisButton>
              </div>
            ) : null}
          </div>
        </Card>

        <Card>
          <div className="p-4 space-y-3">
            <Text as="h2" variant="headingMd">
              {t("assignments", { defaultValue: "Assignments" })}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t("training_unassign_hint", {
                defaultValue:
                  "You can remove users who have not submitted any attempts yet.",
              })}
            </Text>
            <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
              {assignments.length === 0 ? (
                <div className="text-center py-10 px-4 text-sm text-zinc-500">
                  {t("no_assignments_yet", {
                    defaultValue: "No assignments yet.",
                  })}
                </div>
              ) : (
                <DataTable
                  collection={assignments.map((a) => ({
                    ...a,
                    userName: a.user?.name || a.user?.email || "—",
                    lastScore:
                      a.lastAttempt?.scorePercent != null
                        ? `${a.lastAttempt.scorePercent}%`
                        : "—",
                    lastSubmittedAt: a.lastAttempt?.submittedAt
                      ? new Date(a.lastAttempt.submittedAt).toISOString()
                      : "",
                  }))}
                  columns={assignmentColumns}
                />
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">{renderStepNav()}</div>
        </Card>
        </>
        )}
      </div>
    </Page>
  );
}
